import { UniqueIdentifier } from "@dnd-kit/core"
import { AddJobsRecord, Route, RouteJob, RouteSchedule, RoutingType } from "../routingApi"
import { findRouteJobDetails } from "../routingModel"
import dayjs from "dayjs"
import { TenantLocation } from "../../../domain/tenantLocations/tenantLocation"

export const defaultRouteViewState: RouteViewState = {
  route: {
    id: 0,
    name: '',
    active: false,
    routeType: RoutingType.OnDemand,
    crewLeadId: null,
    crewMemberIds: null,
    createdAtUtc: '',
    updatedAtUtc: '',
    jobsCount: null,
    estimatedBreakTimeMinutes: 30,
    dayStartTime: dayjs().toISOString(),
    dayEndTime: dayjs().toISOString(),
    hasBreakTime: false,
    defaultScheduleConfig: null
  },  
  jobs: [],
  schedules: [],
  startingLocation: undefined,
  endingLocation: undefined
}

export type RouteViewState = {
  route: Route
  jobs: RouteJob[]
  schedules: RouteSchedule[]
  startingLocation: RouteLocation | undefined
  endingLocation: RouteLocation | undefined
}

export type RouteLocation = {
  id: string
  jobName: string
  latitude: number
  longitude: number
  address1: string
  address2: string
  city: string
  stateCode: string
  zip: string
}

export type Action =
  | { type: 'ROUTE_LOADED', value: Route }
  | { type: 'ROUTE_JOBS_LOADED', value: RouteJob[] }
  | { type: 'ROUTE_SCHEDULES_LOADED', value: RouteSchedule[] }
  | { type: 'JOB_SORT_INDEX_CHANGED', jobId: number, sortIndex: number }
  | { type: 'ROUTE_ESTIMATED_BREAK_TIME_CHANGED', value: number }
  | { type: 'ROUTE_DAY_START_TIME_CHANGED', value: string }
  | { type: 'ROUTE_DAY_END_TIME_CHANGED', value: string }
  | { type: 'ROUTE_HAS_BREAK_TIME_CHANGED', value: boolean }
  | { type: 'ROUTE_STARTING_LOCATION_CHANGED', value: RouteLocation | undefined }
  | { type: 'ROUTE_ENDING_LOCATION_CHANGED', value: RouteLocation | undefined }

  export const reducer = (state: RouteViewState, action: Action): RouteViewState => {
  switch (action.type) {
    case 'ROUTE_LOADED': {
      const defaultStartTime = dayjs().hour(8).minute(0).second(0);
      const defaultEndTime = dayjs().hour(16).minute(0).second(0);

      const isStartTimeInvalid = !state.route.dayStartTime || !dayjs(state.route.dayStartTime).isValid() || state.route.dayStartTime.startsWith('0001-01-01');
      const isEndTimeInvalid = !state.route.dayEndTime || !dayjs(state.route.dayEndTime).isValid() || state.route.dayEndTime.startsWith('0001-01-01');

      return {
        ...state,
        route: {
          ...action.value,
          dayStartTime: isStartTimeInvalid ? defaultStartTime.toISOString() : action.value.dayStartTime,
          dayEndTime: isEndTimeInvalid ? defaultEndTime.toISOString() : action.value.dayEndTime
        }
      }
    }
    case 'ROUTE_JOBS_LOADED':
      return {
        ...state,
        jobs: [...action.value].sort((a, b) => a.sortIndex - b.sortIndex)
      }
    case 'ROUTE_SCHEDULES_LOADED':
      return {
        ...state,
        schedules: action.value
      }
    case 'JOB_SORT_INDEX_CHANGED':
      return {
        ...state,
        jobs: state.jobs.map(job => ({
          ...job,
          sortIndex: job.id === action.jobId ? action.sortIndex : job.sortIndex
        }))
      }
    case 'ROUTE_ESTIMATED_BREAK_TIME_CHANGED':
      return {
        ...state,
        route: { ...state.route, estimatedBreakTimeMinutes: action.value }
      }
    case 'ROUTE_DAY_START_TIME_CHANGED':
      return {
        ...state,
        route: { ...state.route, dayStartTime: action.value }
      }
    case 'ROUTE_DAY_END_TIME_CHANGED':
      return {
        ...state,
        route: { ...state.route, dayEndTime: action.value }
      }
    case 'ROUTE_HAS_BREAK_TIME_CHANGED':
      return {
        ...state,
        route: { ...state.route, hasBreakTime: action.value }
      }
    case 'ROUTE_STARTING_LOCATION_CHANGED':
      return {
        ...state,
        startingLocation: action.value
      }
    case 'ROUTE_ENDING_LOCATION_CHANGED':
      return {
        ...state,
        endingLocation: action.value
      }
    default:
      return state
  }
}

export const truncateRouteName = (name: string) => {
  if (name.length <= 40) return name;
  return `${name.substring(0, 40)}...`;
};

export const hasRouteChanged = (
  jobs: RouteJob[] | null, 
  routeJobs: RouteJob[] | null, 
) : boolean => {
  if (jobs && routeJobs) {

    if(jobs.length !== routeJobs.length) {
      return true
    }

    for(let i = 0; i < jobs.length; i++) {
      if(jobs[i].jobId !== routeJobs[i].jobId || jobs[i].tenantLocationId !== routeJobs[i].tenantLocationId) {
        return true
      }

      if(jobs[i].workAreaIds !== routeJobs[i].workAreaIds) {
        return true
      }

      if(jobs[i].estimatedDurationInMinutes !== routeJobs[i].estimatedDurationInMinutes) {
        return true
      }

      if(jobs[i].sortIndex !== routeJobs[i].sortIndex) {
        return true
      }
    }

  }

  return false
}

export const dragRouteOrderChanged = (active: UniqueIdentifier, over: UniqueIdentifier, state: RouteViewState) : RouteJob[] => {

    const oldIndex = state.jobs.findIndex((job) => job.sortIndex === active);
    const newIndex = state.jobs.findIndex((job) => job.sortIndex === over);
    
    const newJobs = [...state.jobs];
    const [removed] = newJobs.splice(oldIndex, 1);
    newJobs.splice(newIndex, 0, removed);

    const returnJobs = newJobs.map((job, index) => ({
      ...job,
      sortIndex: index
    }))

    return returnJobs
}

export const buildJobLocations = (
  routeJobs: RouteJob[] | undefined,
  jobsForCreateRoute: AddJobsRecord[] | undefined
): RouteLocation[] => {
  return (routeJobs || []).flatMap(routeJob => {
    const job = findRouteJobDetails(routeJob, jobsForCreateRoute)
    if (!job) {
      return []
    }

    return [{
      id: `job-${routeJob.jobId}`,
      jobName: job.jobName,
      longitude: job.longitude,
      latitude: job.latitude,
      address1: job.address1,
      address2: job.address2,
      city: job.city,
      stateCode: job.state,
      zip: job.zip
    }]
  })
}

export const createMapboxCoordQuery = (
  startingLocation: RouteLocation | undefined, 
  endingLocation: RouteLocation | undefined, 
  jobsForCreateRoute: AddJobsRecord[] | undefined,
  jobs: RouteJob[]) => {
  
    const coords = [];
    if(jobs.length === 0) {
      return []
    }

    if (startingLocation && endingLocation && 
        startingLocation.id === endingLocation.id) {
      const jobsWithCoordinates = jobs?.filter(job => job.jobId !== null) || [];
      if (jobsWithCoordinates.length === 0) {
        return [];
      }
    }

    if (startingLocation && startingLocation.latitude && startingLocation.longitude) {
      coords.push([startingLocation.longitude, startingLocation.latitude]);
    }

    if (jobs) {
      jobs
        .slice()
        .sort((a, b) => a.sortIndex - b.sortIndex)
        .forEach(routeJob => {
          if (routeJob.jobId) {
            const job = findRouteJobDetails(routeJob, jobsForCreateRoute);
            if (job && job.latitude && job.longitude) {
              if (startingLocation && 
                  job.latitude === startingLocation.latitude && 
                  job.longitude === startingLocation.longitude) {
                return;
              }
              if (endingLocation && 
                  job.latitude === endingLocation.latitude && 
                  job.longitude === endingLocation.longitude) {
                return;
              }
              coords.push([job.longitude, job.latitude]);
            }
          }
        });
    }
    
    if (endingLocation && endingLocation.latitude && endingLocation.longitude) {
      coords.push([endingLocation.longitude, endingLocation.latitude]);
    }

    return coords
}

export const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const remainingMinutes = Math.floor((seconds % 3600) / 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
  
  return parts.length > 0 ? parts.join(' ') : '0m';
};

export const formatDistance = (meters: number) => {
  const miles = (meters / 1609.34).toFixed(1);
  return miles;
};

export const getViewStateFromLocation = (loc: RouteLocation | undefined, defaultViewState: { longitude: number; latitude: number; zoom: number }) => ({
  longitude: loc && loc.longitude ? loc.longitude : defaultViewState.longitude,
  latitude: loc && loc.latitude ? loc.latitude : defaultViewState.latitude,
  zoom: defaultViewState.zoom,
  bearing: 0,
  pitch: 0,
  padding: { top: 0, bottom: 0, left: 0, right: 0 }
});

export const prepareRouteJobsForSave = (
  jobs: RouteJob[], 
  routeId: number, 
  startingLocation: RouteLocation | undefined, 
  endingLocation: RouteLocation | undefined
): RouteJob[] => {
  
  let jobsToSave = [...jobs];
  
  if (startingLocation && startingLocation.id.startsWith('shop-')) {
    const tenantLocationId = parseInt(startingLocation.id.replace('shop-', ''));
    
    const existingTenantLocation = jobsToSave.find(job => 
      job.tenantLocationId === tenantLocationId && job.sortIndex === 0
    );
    
    if (!existingTenantLocation) {
      const startingTenantJob: RouteJob = {
        id: 0,
        routeId: routeId,
        jobId: null,
        tenantLocationId: tenantLocationId,
        estimatedDurationInMinutes: 0,
        workAreaIds: '',
        tenantId: 1,
        sortIndex: 0
      };
      
      jobsToSave = jobsToSave.map(job => ({
        ...job,
        sortIndex: job.sortIndex + 1
      }));
      
      jobsToSave.unshift(startingTenantJob);
    }
  } else if (startingLocation && startingLocation.id.startsWith('job-')) {
    const selectedJobId = parseInt(startingLocation.id.replace('job-', ''));
    
    const firstJob = jobsToSave.find(job => job.sortIndex === 0);
    
    if (firstJob && firstJob.tenantLocationId !== null) {
      jobsToSave = jobsToSave.filter(job => job.sortIndex !== 0);
      jobsToSave = jobsToSave.map(job => ({
        ...job,
        sortIndex: job.sortIndex - 1
      }));
    }
    
    if (firstJob && firstJob.jobId !== selectedJobId) {
      const selectedJob = jobsToSave.find(job => job.jobId === selectedJobId);
      const otherJobs = jobsToSave.filter(job => job.jobId !== selectedJobId);
      
      if (selectedJob) {
        jobsToSave = [
          { ...selectedJob, sortIndex: 0 },
          ...otherJobs.map((job, index) => ({ ...job, sortIndex: index + 1 }))
        ];
      }
    }
  }
  
  if (endingLocation && endingLocation.id.startsWith('shop-')) {
    const tenantLocationId = parseInt(endingLocation.id.replace('shop-', ''));
    
    const existingTenantLocation = jobsToSave.find(job => 
      job.tenantLocationId === tenantLocationId && job.sortIndex === jobsToSave.length - 1
    );
    
    if (!existingTenantLocation) {
      const endingTenantJob: RouteJob = {
        id: 0,
        routeId: routeId,
        jobId: null,
        tenantLocationId: tenantLocationId,
        estimatedDurationInMinutes: 0,
        workAreaIds: '',
        tenantId: 1,
        sortIndex: jobsToSave.length
      };
      
      jobsToSave.push(endingTenantJob);
    }
  } else if (endingLocation && endingLocation.id.startsWith('job-')) {
    const selectedJobId = parseInt(endingLocation.id.replace('job-', ''));
    
    const lastJob = jobsToSave.find(job => job.sortIndex === jobsToSave.length - 1);
    
    // If the last job is a tenant location and we're setting ending location to a job, remove the tenant location
    if (lastJob && lastJob.tenantLocationId !== null) {
      jobsToSave = jobsToSave.filter(job => job.sortIndex !== jobsToSave.length - 1);
    }
    
    if (lastJob && lastJob.jobId !== selectedJobId) {
      const selectedJob = jobsToSave.find(job => job.jobId === selectedJobId);
      const otherJobs = jobsToSave.filter(job => job.jobId !== selectedJobId);
      
      if (selectedJob) {
        jobsToSave = [
          ...otherJobs.map((job, index) => ({ ...job, sortIndex: index })),
          { ...selectedJob, sortIndex: otherJobs.length }
        ];
      }
    }
  }
  
  const finalJobsToSave = jobsToSave.map(({ job: _job, ...routeJob }) => ({
    ...routeJob,
    id: routeJob.id || 0,
    routeId: routeId
  }));
  
  return finalJobsToSave;
};

export const getStartingLocation = (jobs: RouteJob[], shopLocation: TenantLocation | undefined, jobsForCreateRoute: AddJobsRecord[] | undefined) : RouteLocation | undefined => {
  if(!shopLocation) {
    return undefined
  }
  
  const shopRouteLocation: RouteLocation = {
    id: `shop-${shopLocation?.id}`,
    latitude: shopLocation?.latitude || 0,
    longitude: shopLocation?.longitude || 0,
    address1: shopLocation?.address1 || '',
    address2: shopLocation?.address2 || '',
    city: shopLocation?.city || '',
    stateCode: shopLocation?.stateCode || '',
    zip: shopLocation?.zip || '',
    jobName: 'My Shop'
  }

  if(jobs.length === 0) {
    return shopRouteLocation
  }

  if(jobs[0].jobId) {
    const job = findRouteJobDetails(jobs[0], jobsForCreateRoute);
    return {
      id: `job-${jobs[0].jobId}`,
      jobName: job?.jobName || '',
      latitude: job?.latitude || 0,
      longitude: job?.longitude || 0,
      address1: job?.address1 || '',
      address2: job?.address2 || '',
      city: job?.city || '',
      stateCode: job?.state || '',
      zip: job?.zip || ''
    }
  }

  return shopRouteLocation
}

export const getEndingLocation = (jobs: RouteJob[], shopLocation: TenantLocation | undefined, jobsForCreateRoute: AddJobsRecord[] | undefined) : RouteLocation | undefined => {
  if(!shopLocation) {
    return undefined
  }
  
  const shopRouteLocation: RouteLocation = {
    id: `shop-${shopLocation?.id}`,
    latitude: shopLocation?.latitude || 0,
    longitude: shopLocation?.longitude || 0,
    address1: shopLocation?.address1 || '',
    address2: shopLocation?.address2 || '',
    city: shopLocation?.city || '',
    stateCode: shopLocation?.stateCode || '',
    zip: shopLocation?.zip || '',
    jobName: 'My Shop'
  }

  if(jobs.length === 0) {
    return shopRouteLocation
  }

  const lastJob = jobs[jobs.length - 1];

  if(lastJob.jobId) {
    const job = findRouteJobDetails(lastJob, jobsForCreateRoute);
    return {
      id: `job-${lastJob.jobId}`,
      jobName: job?.jobName || '',
      latitude: job?.latitude || 0,
      longitude: job?.longitude || 0,
      address1: job?.address1 || '',
      address2: job?.address2 || '',
      city: job?.city || '',
      stateCode: job?.state || '',
      zip: job?.zip || ''
    }
  }

  return shopRouteLocation
}